import { Icon } from '@components/icons';
import { useCopyToClipboard } from '@hooks/helpers/useCopyToClipboard';

import { tokenizeJson } from './json_tokenizer';
import styles from './CodeBlock.module.scss';

export interface CodeBlockProps {
    code: string;
    language: string;
    /** Tokenize `code` as JSON (keys + number/boolean/null literals colored)
        instead of rendering it as plain text. See spec §5.2. */
    json?: boolean;
}

// The portal's one dark, macOS-chrome code panel — extracted from
// IntegratePanel's JS snippet block so every code/JSON surface (sandbox
// response viewer, webhook delivery payloads) shares the same chrome instead
// of falling back to a bare `<pre>`. See dev-portal enterprise redesign spec
// §5.1. Owns its own copy button via the shared useCopyToClipboard hook.
const CodeBlock = ({ code, language, json = false }: CodeBlockProps) => {
    const { copied, copy } = useCopyToClipboard();

    return (
        <div className={styles.code_wrap}>
            <div className={styles.code_bar}>
                <span className={styles.dots}>
                    <span />
                    <span />
                    <span />
                </span>
                <span className={styles.code_lang}>{language}</span>
                <button className={styles.copy} onClick={() => void copy(code)} type="button">
                    <Icon name={copied ? 'filled_check' : 'copy'} width={13} height={13} />
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
            <pre className={styles.code}>{json ? renderJsonTokens(code) : code}</pre>
        </div>
    );
};

function renderJsonTokens(code: string) {
    return tokenizeJson(code).map((token, i) => {
        if (token.type === 'key') {
            return (
                <span key={i} className={styles.tok_key}>
                    {token.text}
                </span>
            );
        }
        if (token.type === 'number' || token.type === 'boolean' || token.type === 'null') {
            return (
                <span key={i} className={styles.tok_literal}>
                    {token.text}
                </span>
            );
        }
        return token.text;
    });
}

export default CodeBlock;
